<template>
    <operator-stats
        :urgent-count="urgentCashoutCount"
        :remaining-time-formatted="remainingTimeFormatted"
        :mid-day-passed="midDayPassed"
    />

    <!-- Urgent alert strip -->
    <div
        v-if="
            (showMidDayCutoff || urgentCashoutCount > 0) &&
            urgentCashoutCount > 0
        "
        class="mb-4 flex items-center justify-between rounded-lg border px-4 py-2.5"
        :class="
            midDayPassed
                ? 'border-red-300 bg-red-50 dark:border-red-500/20 dark:bg-red-500/5'
                : 'border-amber-300 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/5'
        "
    >
        <div class="flex items-center gap-3">
            <ExclamationTriangleIcon
                class="h-4 w-4 flex-shrink-0"
                :class="midDayPassed ? 'text-red-500' : 'text-amber-500'"
            />
            <p
                class="text-sm"
                :class="
                    midDayPassed
                        ? 'text-red-700 dark:text-red-400'
                        : 'text-amber-700 dark:text-amber-400'
                "
            >
                <span class="font-medium"
                    >{{ urgentCashoutCount }} saques pendentes</span
                >
                <span class="text-gray-500 dark:text-gray-400"> · </span>
                <span v-if="!midDayPassed">
                    <span class="font-mono text-xs">{{
                        remainingTimeFormatted
                    }}</span>
                    restantes
                </span>
                <span v-else>prazo das 12h excedido</span>
            </p>
        </div>
        <button
            class="flex-shrink-0 rounded-md px-3 py-1 text-xs font-medium transition-colors"
            :class="
                midDayPassed
                    ? 'text-red-700 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-500/10'
                    : 'text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-500/10'
            "
            @click="filterUrgentReviews"
        >
            {{ isFilteringUrgent ? "Mostrar todos" : "Ver urgentes" }}
        </button>
    </div>

    <!-- Filters -->
    <filters
        ref="filtersComponent"
        v-model="filters"
        @fetch="onFilterFetch"
        status-filter
        hide-buttons
        :default-status="['pending']"
        :statuses="statusOptions"
    >
        <template #inline="{ filters: innerFilters }">
            <Menu as="div" class="relative">
                <MenuButton
                    class="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
                    :class="
                        innerFilters.status.length > 0
                            ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                            : 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-600'
                    "
                >
                    <FunnelIcon class="h-3.5 w-3.5" />
                    <span>{{ statusLabel(innerFilters.status) }}</span>
                    <ChevronDownIcon class="h-3 w-3 opacity-50" />
                </MenuButton>
                <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                >
                    <MenuItems
                        class="absolute left-0 z-10 mt-1 w-44 origin-top-left rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-none dark:border-gray-600 dark:bg-gray-700"
                    >
                        <MenuItem
                            v-for="opt in statusOptions"
                            :key="opt.value"
                            v-slot="{ active }"
                        >
                            <button
                                type="button"
                                class="flex w-full items-center gap-2 px-3 py-2 text-xs"
                                :class="
                                    active ? 'bg-gray-50 dark:bg-gray-600' : ''
                                "
                                @click.prevent.stop="
                                    toggleStatus(innerFilters, opt.value)
                                "
                            >
                                <CheckIcon
                                    v-if="
                                        innerFilters.status.includes(opt.value)
                                    "
                                    class="h-3.5 w-3.5 text-gray-900 dark:text-white"
                                />
                                <span v-else class="h-3.5 w-3.5" />
                                <span
                                    class="font-medium"
                                    :class="
                                        innerFilters.status.includes(opt.value)
                                            ? 'text-gray-900 dark:text-white'
                                            : 'text-gray-500 dark:text-gray-400'
                                    "
                                >
                                    {{ opt.label }}
                                </span>
                            </button>
                        </MenuItem>
                    </MenuItems>
                </transition>
            </Menu>
            <input
                placeholder="Min Approved"
                v-model="minApproved"
                type="number"
                class="ml-2 w-28 rounded-md border-0 bg-gray-100 px-3 py-1.5 text-xs text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-gray-400 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:placeholder:text-gray-500 dark:focus:ring-gray-500"
            />
        </template>

        <template #actions>
            <button
                type="button"
                class="rounded-md px-3 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                @click.prevent="clearAndFetch"
            >
                Limpar
            </button>
            <button
                type="button"
                class="rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                @click.prevent="onFilterFetch"
            >
                Filtrar
            </button>
            <span
                class="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1.5 font-mono text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600"
            >
                {{ pagination.meta.total }}
            </span>
            <button
                class="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
                :class="
                    isFilteringUrgent
                        ? 'bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-300 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20'
                        : 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-600'
                "
                @click="filterUrgentReviews"
                v-tooltip="
                    'Saques que precisam ser processados até 12h de hoje'
                "
            >
                <ClockIcon class="h-3.5 w-3.5" />
                {{ isFilteringUrgent ? "Todos" : "Até 12h" }}
            </button>
        </template>
    </filters>

    <!-- Content -->
    <div class="mt-1">
        <empty-state v-if="!loading && reviews.length < 1" />
        <div v-else>
            <div
                v-if="skippedReviewIds.length > 0"
                class="mb-2 flex items-center justify-end"
            >
                <button
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-600"
                    v-tooltip="
                        'Saques pulados voltam para o fim da fila. Clique para rever agora'
                    "
                    @click="clearSkipped"
                >
                    Pulados: {{ skippedReviewIds.length }} · rever
                </button>
            </div>

            <review-decision-panel
                ref="reviewDecisionPanel"
                :review="selectedReview"
                :payable-details="selectedPayableDetails"
                :is-same-day-urgent="
                    selectedReview ? isSameDayUrgent(selectedReview) : false
                "
                :is-skipped="
                    selectedReview
                        ? skippedReviewIds.includes(selectedReview.id)
                        : false
                "
                :action-processing="actionProcessing"
                @skip="skipSelected"
                @refresh="handleReviewCompleted"
            />
        </div>
    </div>
</template>

<script>
import { ClockIcon } from "@heroicons/vue/20/solid";
import {
    ExclamationTriangleIcon,
    FunnelIcon,
    ChevronDownIcon,
    CheckIcon,
} from "@heroicons/vue/24/outline";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue";
import Filters from "../../components/layouts/Filters.vue";
import EmptyState from "../../components/layouts/EmptyState.vue";
import OperatorStats from "../../components/cashouts/reviews/OperatorStats.vue";
import ReviewDecisionPanel from "../../components/cashouts/reviews/ReviewDecisionPanel.vue";
import { useToast } from "vue-toastification";
import axios from "axios";

export default {
    name: "ReviewsCockpit",
    components: {
        ReviewDecisionPanel,
        OperatorStats,
        EmptyState,
        ExclamationTriangleIcon,
        FunnelIcon,
        ChevronDownIcon,
        CheckIcon,
        Menu,
        MenuButton,
        MenuItems,
        MenuItem,
        Filters,
        ClockIcon,
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            statusOptions: [
                { label: "Solicitado", value: "pending" },
                { label: "Aprovado", value: "approved" },
                { label: "Reprovado", value: "rejected" },
            ],
            loading: false,
            loadingUrgentCount: false,
            urgentCashoutCount: 0,
            reviews: [],
            selectedReviewId: null,
            // Vindo da fila de decisões (?review=ID): consumido na primeira
            // carga; se o saque não estiver na página atual, cai no padrão.
            deepLinkReviewId: null,
            skippedReviewIds: JSON.parse(
                localStorage.getItem("cockpit-skipped-reviews") || "[]",
            ),
            filters: {},
            minApproved: "",
            isFilteringUrgent: false,
            remainingTime: 0,
            midDayPassed: false,
            timerInterval: null,
            pagination: {
                meta: {
                    current_page: 1,
                    last_page: 10,
                    page: 1,
                    per_page: 10,
                    total: 0,
                },
            },
            payableDetails: {},
            actionProcessing: false,
        };
    },
    computed: {
        remainingTimeFormatted() {
            if (this.midDayPassed) {
                return "00:00:00";
            }

            const hours = Math.floor(this.remainingTime / 3600);
            const minutes = Math.floor((this.remainingTime % 3600) / 60);
            const seconds = this.remainingTime % 60;

            return [
                hours.toString().padStart(2, "0"),
                minutes.toString().padStart(2, "0"),
                seconds.toString().padStart(2, "0"),
            ].join(":");
        },
        showMidDayCutoff() {
            const now = new Date();
            const day = now.getDay();
            return day >= 1 && day <= 5;
        },
        orderedReviews() {
            return [...this.reviews].sort((a, b) => {
                const skippedDiff =
                    (this.skippedReviewIds.includes(a.id) ? 1 : 0) -
                    (this.skippedReviewIds.includes(b.id) ? 1 : 0);
                if (skippedDiff !== 0) {
                    return skippedDiff;
                }

                const urgentDiff =
                    (this.isSameDayUrgent(b) ? 0 : 1) -
                    (this.isSameDayUrgent(a) ? 0 : 1);
                if (urgentDiff !== 0) {
                    return urgentDiff;
                }

                const scoreA = a?.seller?.score ?? 100;
                const scoreB = b?.seller?.score ?? 100;
                if (scoreA !== scoreB) {
                    return scoreA - scoreB;
                }

                const dateA = new Date(a.created_at).getTime();
                const dateB = new Date(b.created_at).getTime();
                return dateA - dateB;
            });
        },
        selectedReview() {
            if (!this.selectedReviewId) {
                return null;
            }

            return (
                this.reviews.find(
                    (review) => review.id === this.selectedReviewId,
                ) || null
            );
        },
        selectedPayableDetails() {
            if (!this.selectedReview) {
                return null;
            }

            return this.payableDetails[this.selectedReview.cashout.id] || null;
        },
    },
    mounted() {
        this.deepLinkReviewId = Number(this.$route.query.review) || null;
        this.filters.status = ["pending"];
        this.startMidDayTimer();
        setTimeout(() => {
            this.fetch();
            this.fetchUrgentCount();
        }, 500);
    },
    beforeDestroy() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    },
    methods: {
        statusLabel(selected) {
            if (!selected || selected.length === 0) {
                return "Status";
            }

            if (selected.length === this.statusOptions.length) {
                return "Todos";
            }

            return selected
                .map(
                    (v) => this.statusOptions.find((o) => o.value === v)?.label,
                )
                .filter(Boolean)
                .join(", ");
        },
        toggleStatus(innerFilters, value) {
            const idx = innerFilters.status.indexOf(value);
            if (idx >= 0) {
                innerFilters.status.splice(idx, 1);
            } else {
                innerFilters.status.push(value);
            }
        },
        clearAndFetch() {
            this.minApproved = "";
            this.isFilteringUrgent = false;
            this.pagination.meta.current_page = 1;
            if (this.$refs.filtersComponent) {
                this.$refs.filtersComponent.clearFilters();
            }
        },
        onFilterFetch() {
            this.pagination.meta.current_page = 1;
            this.actionProcessing = true;
            this.fetch().finally(() => {
                this.actionProcessing = false;
            });
        },
        startMidDayTimer() {
            let lastCountUpdate = 0;

            const updateTimer = () => {
                const now = new Date();
                const midDay = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    12,
                    0,
                    0,
                );

                if (now >= midDay) {
                    this.midDayPassed = true;
                    this.remainingTime = 0;
                } else {
                    this.midDayPassed = false;
                    this.remainingTime = Math.floor((midDay - now) / 1000);
                }

                const currentTime = Math.floor(Date.now() / 1000);
                if (
                    (this.midDayPassed || this.remainingTime < 1800) &&
                    currentTime - lastCountUpdate > 60
                ) {
                    this.fetchUrgentCount();
                    lastCountUpdate = currentTime;
                }
            };

            updateTimer();
            this.timerInterval = setInterval(updateTimer, 1000);
        },
        async fetch() {
            this.loading = true;

            const requestFilters = {
                ...this.filters,
                "filter[minApproved]": this.minApproved,
            };

            if (this.isFilteringUrgent) {
                requestFilters["filter[urgentMidDay]"] = true;
            }

            try {
                const { data } = await axios.get("/api/cashout/review", {
                    params: {
                        page: this.pagination.meta.current_page,
                        per_page: this.pagination.meta.per_page,
                        ...requestFilters,
                    },
                });

                this.reviews = data.data;
                this.pagination = data;
                this.ensureSelection();

                if (this.selectedReview) {
                    await this.loadPayableDetails(this.selectedReview);
                }
            } catch (error) {
                console.error(
                    "❌ [ReviewsCockpit] Erro ao buscar reviews:",
                    error,
                );
            } finally {
                this.loading = false;
            }
        },
        fetchUrgentCount() {
            this.loadingUrgentCount = true;

            axios
                .get("/api/cashout/review/count", {
                    params: {
                        "filter[status]": "pending",
                    },
                })
                .then(({ data }) => {
                    this.urgentCashoutCount = data.count || 0;
                })
                .catch((error) => {
                    console.error(
                        "Erro ao buscar contagem de saques urgentes:",
                        error,
                    );

                    if (this.isFilteringUrgent) {
                        this.urgentCashoutCount = this.pagination.meta.total;
                    }
                })
                .finally(() => {
                    this.loadingUrgentCount = false;
                });
        },
        filterUrgentReviews() {
            this.isFilteringUrgent = !this.isFilteringUrgent;
            this.pagination.meta.current_page = 1;
            this.actionProcessing = true;
            this.fetch().finally(() => {
                this.actionProcessing = false;
                setTimeout(() => this.fetchUrgentCount(), 500);
            });
        },
        isSameDayUrgent(review) {
            // Prazo do servidor (CashoutDeadline, mesma régua da fila de
            // decisões): urgente quando vence até o fim de hoje. O cálculo
            // JS abaixo fica só como fallback para payloads sem due_at.
            if (review.due_at) {
                const due = new Date(review.due_at.replace(" ", "T"));
                const endOfToday = new Date();
                endOfToday.setHours(23, 59, 59, 999);

                return due <= endOfToday;
            }

            const reviewDate = new Date(review.created_at);
            const now = new Date();
            const today = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
            );
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            const reviewDay = reviewDate.getDay();
            const reviewHour = reviewDate.getHours();

            if (reviewDate >= today && reviewHour < 12) {
                return true;
            }

            if (reviewDate >= yesterday && reviewHour >= 12) {
                const yesterdayDay = yesterday.getDay();
                return yesterdayDay >= 1 && yesterdayDay <= 4;
            }

            if (reviewDate >= yesterday) {
                const dayOfWeek = reviewDate.getDay();

                if (dayOfWeek === 5 && reviewHour >= 12) {
                    return true;
                }

                if (dayOfWeek === 6 || dayOfWeek === 0) {
                    return true;
                }
            }

            return false;
        },
        loadPayableDetails(review) {
            if (!review || !review.cashout) {
                return Promise.resolve(null);
            }

            const cashoutId = review.cashout.id;

            if (this.payableDetails[cashoutId]) {
                return Promise.resolve(this.payableDetails[cashoutId]);
            }

            return axios
                .get(`/api/cashout/${cashoutId}/payables/details`)
                .then(({ data }) => {
                    this.payableDetails = {
                        ...this.payableDetails,
                        [cashoutId]: data,
                    };
                    return data;
                })
                .catch((error) => {
                    console.error(
                        `❌ [ReviewsCockpit] Erro ao buscar detalhes do cashout ${cashoutId}:`,
                        error,
                    );
                    return null;
                });
        },
        ensureSelection() {
            if (!this.reviews.length) {
                this.selectedReviewId = null;
                return;
            }

            if (this.deepLinkReviewId) {
                const deepLinkId = this.deepLinkReviewId;
                const target = this.reviews.find(
                    (review) => review.id === deepLinkId,
                );

                this.deepLinkReviewId = null;
                this.clearReviewQuery();

                if (target) {
                    this.selectedReviewId = target.id;
                    return;
                }

                // Fora da página atual da fila: avisa em vez de trocar de
                // saque silenciosamente com o ?review= ainda na URL.
                this.toast.warning(
                    `Saque da revisão #${deepLinkId} não está na página atual da fila.`,
                );
            }

            const stillSelected = this.reviews.some(
                (review) => review.id === this.selectedReviewId,
            );

            if (!stillSelected) {
                const nextReview = this.orderedReviews[0];
                this.selectedReviewId = nextReview ? nextReview.id : null;
            }
        },
        clearReviewQuery() {
            if (!this.$route.query.review) {
                return;
            }

            const { review, ...query } = this.$route.query;
            this.$router.replace({ query });
        },

        selectReview(review) {
            if (!review) {
                return;
            }

            this.selectedReviewId = review.id;
            this.loadPayableDetails(review);
        },
        skipSelected() {
            if (!this.selectedReview) {
                return;
            }

            const skippedId = this.selectedReview.id;

            if (!this.skippedReviewIds.includes(skippedId)) {
                this.skippedReviewIds = [...this.skippedReviewIds, skippedId];
                this.persistSkipped();
            }

            const next = this.orderedReviews.find(
                (review) => review.id !== skippedId,
            );

            if (next) {
                this.selectReview(next);
            }
        },
        clearSkipped() {
            this.skippedReviewIds = [];
            this.persistSkipped();
            this.ensureSelection();
        },
        forgetSkipped(reviewId) {
            if (this.skippedReviewIds.includes(reviewId)) {
                this.skippedReviewIds = this.skippedReviewIds.filter(
                    (id) => id !== reviewId,
                );
                this.persistSkipped();
            }
        },
        persistSkipped() {
            localStorage.setItem(
                "cockpit-skipped-reviews",
                JSON.stringify(this.skippedReviewIds),
            );
        },
        async handleReviewCompleted(review) {
            const processedReview = review || this.selectedReview;
            this.actionProcessing = true;

            if (processedReview) {
                this.forgetSkipped(processedReview.id);
            }

            if (processedReview && this.isSameDayUrgent(processedReview)) {
                this.urgentCashoutCount = Math.max(
                    0,
                    this.urgentCashoutCount - 1,
                );
            }

            try {
                await this.fetch();
                this.fetchUrgentCount();

                if (this.$refs.reviewDecisionPanel) {
                    await this.$refs.reviewDecisionPanel.reloadCashoutChecklist();
                }
            } finally {
                this.actionProcessing = false;
            }
        },
    },
};
</script>
